import { Space, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";

const { Text } = Typography;

export const columnSorterFunc = (a, b, field) => {
    const valA = a?.[field];
    const valB = b?.[field];
    if (valA === valB) return 0;
    if (valA == null) return -1;
    if (valB == null) return 1;
    if (typeof valA === "number" && typeof valB === "number") return valA - valB;
    return String(valA).localeCompare(String(valB));
};

// Only Elevated and High are coloured. None and Insufficient evidence stay neutral on purpose —
// they are not findings, and a screen full of coloured tags trains people to ignore the colour.
const LEVEL_COLOURS = {
    High: "red",
    Elevated: "orange",
};

export const columnDef_contact = {
    title: "Contact",
    dataIndex: "displayName",
    key: "displayName",
    sorter: (a, b) => columnSorterFunc(a, b, "displayName"),
    render: (_, record) => (
        <Space direction="vertical" size={0}>
            <Text>{record?.displayName}</Text>
            <Text type="secondary" copyable={{ text: record?.email ?? "" }}>
                {record?.email ?? "—"}
            </Text>
        </Space>
    ),
};

export const columnDef_accountId = {
    title: "Account",
    dataIndex: "accountId",
    key: "accountId",
    width: "110px",
    sorter: (a, b) => columnSorterFunc(a, b, "accountId"),
    render: (text) => <Text copyable={{ text: String(text) }}>{text}</Text>,
};

export const columnDef_level = {
    title: "Status",
    dataIndex: "levelName",
    key: "levelName",
    width: "170px",
    sorter: (a, b) => columnSorterFunc(a, b, "score"),
    render: (levelName, record) => (
        <Space direction="vertical" size={0}>
            <Tag color={LEVEL_COLOURS[levelName]}>{levelName}</Tag>
            {record?.sampledForAudit ? (
                <Tooltip title="Scored clean. Included at random so we can measure what the rules miss — not a finding.">
                    <Tag>audit sample</Tag>
                </Tooltip>
            ) : null}
        </Space>
    ),
};

export const columnDef_score = {
    title: "Score",
    dataIndex: "score",
    key: "score",
    width: "90px",
    sorter: (a, b) => columnSorterFunc(a, b, "score"),
    // Null is not zero. A contact with too little data to judge has no score, and showing 0 would
    // read as "clean" — the exact conflation the levels exist to prevent.
    render: (score) =>
        score == null ? <Text type="secondary">—</Text> : <Text>{score}</Text>,
};

export const columnDef_reasons = {
    title: "Why",
    dataIndex: "reasonsJson",
    key: "reasonsJson",
    render: (reasonsJson) => {
        if (!reasonsJson) return <Text type="secondary">—</Text>;

        let reasons;
        try {
            reasons = JSON.parse(reasonsJson);
        } catch {
            return <Text type="secondary">unreadable</Text>;
        }
        if (!Array.isArray(reasons) || reasons.length === 0)
            return <Text type="secondary">—</Text>;

        return (
            <Space size={4} wrap>
                {reasons.map((r) => (
                    <Tooltip
                        key={r?.Code ?? r?.code}
                        title={Object.entries(r?.Evidence ?? r?.evidence ?? {})
                            .map(([k, v]) => `${k}: ${v}`)
                            .join("\n")}
                    >
                        <Tag>{r?.Code ?? r?.code}</Tag>
                    </Tooltip>
                ))}
            </Space>
        );
    },
};

export const columnDef_review = {
    title: "Reviewed",
    dataIndex: "reviewOutcomeName",
    key: "reviewOutcomeName",
    width: "150px",
    sorter: (a, b) => columnSorterFunc(a, b, "reviewOutcomeName"),
    render: (outcome, record) =>
        outcome ? (
            <Space direction="vertical" size={0}>
                <Tag color={outcome === "Confirmed" ? "red" : "green"}>{outcome}</Tag>
                <Text type="secondary">
                    {record?.reviewedDate
                        ? dayjs(record.reviewedDate).format("YYYY-MM-DD")
                        : ""}
                </Text>
            </Space>
        ) : (
            <Text type="secondary">not reviewed</Text>
        ),
};

export const columnDef_computedDate = {
    title: "Scored",
    dataIndex: "computedDate",
    key: "computedDate",
    width: "130px",
    sorter: (a, b) => columnSorterFunc(a, b, "computedDate"),
    render: (value) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—"),
};
